import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createBooking, getProducts, validatePromo } from '../lib/api';
import Analytics from '../utils/analytics';
import './BookingWizard.css';

import ServiceSelection from './wizard/ServiceSelection';
import LocationInput from './wizard/LocationInput';
import TimePicker from './wizard/TimePicker';
import BookingSummary from './wizard/BookingSummary';

const PENDING_BOOKING_KEY = 'glaminant_pending_booking';

export default function BookingWizard({ startStep }) {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine initial step: if startStep is provided (e.g. /book/location), start there
    const initialStep = startStep || 1;
    const [step, setStep] = useState(initialStep);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [restoringCart, setRestoringCart] = useState(initialStep >= 2);

    // Package-based state
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [selectedAddOns, setSelectedAddOns] = useState([]);
    const [serviceLocation, setServiceLocation] = useState(null);
    const [appointment, setAppointment] = useState({ date: '', slot: '', artisanId: null });

    // Promo Code State
    const [promoCode, setPromoCode] = useState('');
    const [promoDiscount, setPromoDiscount] = useState(0);
    const [promoError, setPromoError] = useState('');
    const [promoApplied, setPromoApplied] = useState(false);

    // Derived totals
    const totalPriceCents = Math.max(0, (selectedPackage?.priceCents || 0) +
        selectedAddOns.reduce((sum, a) => sum + a.priceCents, 0) - promoDiscount);
    const totalDurationMinutes = (selectedPackage?.durationMinutes || 0) +
        selectedAddOns.reduce((sum, a) => sum + a.durationMinutes, 0);

    // Restore cart from localStorage when entering at step >= 2 (post-auth)
    useEffect(() => {
        if (initialStep >= 2) {
            restoreCartFromStorage();
        }
    }, []);

    const restoreCartFromStorage = async () => {
        setRestoringCart(true);
        try {
            const saved = localStorage.getItem(PENDING_BOOKING_KEY);
            if (!saved) {
                // No pending booking — redirect back to package selection
                navigate('/book');
                return;
            }

            const pending = JSON.parse(saved);
            // Fetch full package data from API to get complete objects
            const allPackages = await getProducts();

            const pkg = allPackages.find(p => p.id === pending.packageId);
            if (!pkg) {
                setError('Your selected package is no longer available.');
                navigate('/book');
                return;
            }

            setSelectedPackage(pkg);

            // Restore add-ons by matching names
            if (pending.addons && pending.addons.length > 0 && pkg.addOns) {
                const restored = pending.addons
                    .map(name => pkg.addOns.find(a => a.name === name))
                    .filter(Boolean);
                setSelectedAddOns(restored);
            }
        } catch (err) {
            console.error('Failed to restore cart:', err);
            setError('Failed to restore your selection. Please try again.');
            navigate('/book');
        } finally {
            setRestoringCart(false);
        }
    };

    const handlePackageSelect = (pkg, addOns) => {
        setSelectedPackage(pkg);
        setSelectedAddOns(addOns || []);
    };

    // Auth-gated navigation
    const handleNext = () => {
        if (step === 1) {
            const token = localStorage.getItem('token');
            if (!token) {
                // Save selection to localStorage for post-auth restoration
                const pendingBooking = {
                    packageId: selectedPackage.id,
                    addons: selectedAddOns.map(a => a.name)
                };
                localStorage.setItem(PENDING_BOOKING_KEY, JSON.stringify(pendingBooking));
                // Redirect to login with return URL
                navigate('/login?redirect=/book/location');
                return;
            }
        }
        setStep(s => s + 1);
    };

    const handleBack = () => setStep(s => s - 1);

    const handleApplyPromo = async () => {
        if (!promoCode.trim()) {
            setPromoError('Please enter a promo code');
            return;
        }
        setLoading(true); // Re-use loading state or create new local loading?
        // Better to use separate loading if we don't want to block whole UI, 
        // but re-using is fine for MVP as it disables Next button.
        setPromoError('');
        setPromoDiscount(0);
        setPromoApplied(false);

        try {
            const promo = await validatePromo(promoCode);

            // Calculate discount
            let discount = 0;
            const basePrice = (selectedPackage?.priceCents || 0); // Logic should match backend or likely be done on backend return?
            // Backend validates but returns promo details. We calculate logic here for display.

            if (promo.discountType === 'PERCENTAGE') {
                discount = Math.round(basePrice * promo.discountValue / 100);
            } else {
                discount = promo.discountValue;
            }

            setPromoDiscount(discount);
            setPromoApplied(true);

            // Analytics: Track promo application
            Analytics.pushCustomEvent('apply_promo', {
                coupon: promoCode,
                discount: discount / 100
            });
        } catch (err) {
            setPromoError(err.message);
            setPromoApplied(false);
        } finally {
            setLoading(false);
        }
    };

    // Step 4: Final Confirmation
    const handleConfirmBooking = async () => {
        setLoading(true);
        setError('');
        try {
            const startTime = `${appointment.date}T${appointment.slot}`;

            await createBooking({
                artisanId: appointment.artisanId,
                serviceId: selectedPackage.id,
                startTime: startTime,
                promoCode: promoApplied ? promoCode : null,
                location: {
                    address: serviceLocation.address,
                    lat: serviceLocation.lat,
                    lng: serviceLocation.lng
                },
                accessInstructions: serviceLocation.accessInstructions
            });

            // Analytics: Track e-commerce purchase
            const items = [
                { item_name: selectedPackage.name, price: selectedPackage.priceCents / 100 },
                ...selectedAddOns.map(a => ({ item_name: a.name, price: a.priceCents / 100 }))
            ];

            Analytics.pushEcommerceEvent('purchase', {
                transaction_id: `temp_${Date.now()}`, // Fallback if backend doesn't return ID immediately
                value: totalPriceCents / 100,
                currency: "CAD",
                coupon: promoApplied ? promoCode : null,
                items: items
            });

            // Clean up pending booking from localStorage
            localStorage.removeItem(PENDING_BOOKING_KEY);

            alert('Booking Requested! 🎉 Your artisan will confirm shortly.');
            navigate('/dashboard');
        } catch (err) {
            console.error(err);
            setError(err.message || 'Booking failed');
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = () => {
        switch (step) {
            case 1:
                return (
                    <ServiceSelection
                        selectedPackage={selectedPackage}
                        selectedAddOns={selectedAddOns}
                        onSelect={handlePackageSelect}
                    />
                );
            case 2:
                return (
                    <LocationInput
                        location={serviceLocation}
                        onLocationSelect={setServiceLocation}
                    />
                );
            case 3:
                return (
                    <TimePicker
                        selectedServices={selectedPackage ? [{ id: selectedPackage.id }] : []}
                        totalDurationOverride={totalDurationMinutes}
                        location={serviceLocation}
                        onSlotSelect={setAppointment}
                    />
                );
            case 4:
                return (
                    <BookingSummary
                        selectedPackage={selectedPackage}
                        selectedAddOns={selectedAddOns}
                        totalPriceCents={totalPriceCents}
                        totalDurationMinutes={totalDurationMinutes}
                        location={serviceLocation}
                        date={appointment.date}
                        slot={appointment.slot}
                        promoCode={promoCode}
                        onPromoCodeChange={setPromoCode}
                        onApplyPromo={handleApplyPromo}
                        promoDiscount={promoDiscount}
                        promoError={promoError}
                        promoApplied={promoApplied}
                    />
                );
            default:
                return <div>Unknown Step</div>;
        }
    };

    // Validation for Next Button
    const canProceed = () => {
        if (step === 1) return selectedPackage !== null;
        if (step === 2) return serviceLocation !== null;
        if (step === 3) return appointment.date && appointment.slot;
        return true;
    };

    const stepLabels = ['Package', 'Location', 'Schedule', 'Review'];

    if (restoringCart) {
        return (
            <div className="booking-wizard-container">
                <div className="wizard-loading">
                    <div className="loading-spinner"></div>
                    <p>Restoring your selection...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="booking-wizard-container">
            {error && <div className="error-banner">{error}</div>}

            <div className="wizard-progress">
                {stepLabels.map((label, i) => {
                    const s = i + 1;
                    return (
                        <div key={s} className="step-wrapper">
                            <div className={`step-dot ${step >= s ? 'active' : ''}`}>{s}</div>
                            <span className="step-label">{label}</span>
                            {s < 4 && <div className={`step-line ${step > s ? 'active' : ''}`}></div>}
                        </div>
                    );
                })}
            </div>

            {/* Cart Summary sidebar — shows on steps 2-4 */}
            {step >= 2 && selectedPackage && (
                <div className="cart-summary-bar">
                    <div className="cart-summary-content">
                        <span className="cart-pkg-name">📦 {selectedPackage.name}</span>
                        {selectedAddOns.length > 0 && (
                            <span className="cart-addons">
                                + {selectedAddOns.map(a => a.name).join(', ')}
                            </span>
                        )}
                        <span className="cart-total">
                            ${(totalPriceCents / 100).toFixed(2)} · {totalDurationMinutes} min
                        </span>
                    </div>
                </div>
            )}

            <div className="wizard-content">
                {renderStepContent()}
            </div>

            <div className="wizard-actions">
                {step > 1 && (
                    <button className="btn btn-secondary" onClick={handleBack} disabled={loading}>
                        Back
                    </button>
                )}

                {step < 4 ? (
                    <button
                        className="btn btn-primary"
                        onClick={handleNext}
                        disabled={!canProceed()}
                    >
                        Next
                    </button>
                ) : (
                    <button
                        className="btn btn-primary"
                        onClick={handleConfirmBooking}
                        disabled={loading}
                    >
                        {loading ? 'Requesting...' : 'Request Appointment'}
                    </button>
                )}
            </div>
        </div>
    );
}
